import ThingEntity from '@/entities/ThingEntity';
import * as L from 'leaflet';
import StateService from '@/services/StateService';

export default class GamePlayService {

    public static readonly DIGGING_RADIUS = 20;
    public static readonly AUTO_SCANNING_RADIUS = 100;

    private static readonly PROBABILITY_OF_FINDING = 0.1;
    private static readonly MAX_NUMBER_OF_FINDING_THINGS = 3;
    private static readonly MAX_THING_VALUE = 100;

    private static readonly DEFAULT_MAP_ZOOM = 17;

    private static isDiceRolled(probability: number): boolean {
        const diceRollResult = Math.random();
        return diceRollResult <= probability;
    }

    private static randomFromInterval(from: number, to: number): number {
        const length = to - from;
        const ratio = Math.random();
        return length * ratio * ratio + from;
    }

    private static search(latitude: number, longitude: number): ThingEntity[] {
        if (!GamePlayService.isDiceRolled(GamePlayService.PROBABILITY_OF_FINDING)) {
            return [];
        }
        const numberOfThings = Math.round(
            GamePlayService.randomFromInterval(0, GamePlayService.MAX_NUMBER_OF_FINDING_THINGS),
        );
        const resultedNumberOfThings = numberOfThings || 1;
        const things = [];
        for (let i = 0; i < resultedNumberOfThings; i++) {
            const radius = 0.001;
            const thing = new ThingEntity(
                latitude - radius + 2 * Math.random() * radius,
                longitude - radius + 2 * Math.random() * radius,
                Math.round(Math.random() * GamePlayService.MAX_THING_VALUE),
            );
            things.push(thing);
        }
        return things;
    }

    private stateService: StateService;

    private map?: L.Map;
    private latitude: number = 51.508;
    private longitude: number = -0.11;
    private notificationCallback?: (message: string) => void;
    private clickThingCallback?: (thing: ThingEntity) => void;

    constructor(stateService: StateService) {
        this.stateService = stateService;
    }

    public init(
        mapElementId: string,
        notificationCallback: (message: string) => void,
        clickThingCallback: (thing: ThingEntity) => void,
    ): void {
        this.initMap(mapElementId);
        this.notificationCallback = notificationCallback;
        this.clickThingCallback = clickThingCallback;
    }

    public async digUp(progressCallback: (progress: number) => void): Promise<void> {
        return await new Promise((resolve) => {
            let progress = 0;
            const intervalHandler = setInterval(() => {
                progress++;
                progressCallback(progress);
                if (progress >= 100) {
                    clearInterval(intervalHandler);
                    resolve();
                }
            }, 100);
        });
    }

    public pickUp(thing: ThingEntity): void {
        this.stateService.coins += thing.value;
        thing.marker.remove();
    }

    private initMap(elementId: string): void {
        this.map = L.map(elementId, {
            attributionControl: false,
            zoomControl: false,
        }).setView([0, 0], GamePlayService.DEFAULT_MAP_ZOOM);
        const circleOfDigging = L.circle([this.latitude, this.longitude], {
            weight: 1,
            color: 'red',
            fillColor: 'red',
            fillOpacity: 0.1,
            radius: GamePlayService.DIGGING_RADIUS,
        }).addTo(this.map);
        const circleOfView = L.circle([this.latitude, this.longitude], {
            weight: 1,
            color: 'blue',
            fillColor: 'blue',
            fillOpacity: 0.1,
            radius: GamePlayService.AUTO_SCANNING_RADIUS,
        }).addTo(this.map);
        L.tileLayer('https://api.tiles.mapbox.com/v4/{id}/{z}/{x}/{y}.png?access_token={accessToken}', {
            maxZoom: 18,
            id: 'mapbox.streets',
            accessToken: 'pk.eyJ1Ijoic2x5c2hraW4iLCJhIjoiY2sxamFlejNyMDk0czNtbDl2eG9ka29tayJ9._N607LoeEzzn995o_DZHug',
        }).addTo(this.map);

        const geoOptions = {
            enableHighAccuracy: true,
        };

        navigator.geolocation.watchPosition((position) => {
            this.latitude = position.coords.latitude;
            this.longitude = position.coords.longitude;
            if (this.map === undefined) {
                return;
            }
            this.map.setView([this.latitude, this.longitude], this.map.getZoom());
            // marker.setLatLng([this.latitude, this.longitude]);
            circleOfDigging.setLatLng([this.latitude, this.longitude]);
            circleOfView.setLatLng([this.latitude, this.longitude]);
        }, undefined, geoOptions);

        setInterval(() => {
            const things = GamePlayService.search(this.latitude, this.longitude);
            this.addThingsToMap(things);
        }, 1000);
    }

    private addThingsToMap(things: ThingEntity[]): void {
        things.forEach((thing: ThingEntity) => {
            if (this.map === undefined) {
                return;
            }
            thing.marker.addTo(this.map);
            const intervalHandler = setInterval(() => {
                const maxLifeTime = 60;
                const lifeTime = new Date().getTime() / 1000 - thing.creationTime;
                if (lifeTime > maxLifeTime) {
                    thing.marker.remove();
                    clearInterval(intervalHandler);
                }
                thing.marker.setStyle({fillOpacity: 1 - lifeTime / maxLifeTime});
            }, 1000);

            thing.marker.on('click', () => {
                if (this.map === undefined) {
                    return;
                }
                const distance = this.map.distance(
                    [this.latitude, this.longitude],
                    [thing.latitude, thing.longitude],
                );
                if (distance > GamePlayService.DIGGING_RADIUS) {
                    return this.notify('You should be closer to pick up the treasure');
                }
                this.clickThing(thing);
            });
        });
    }

    private notify(message: string): void {
        if (this.notificationCallback === undefined) {
            return;
        }
        this.notificationCallback(message);
    }

    private clickThing(thing: ThingEntity): void {
        if (this.clickThingCallback === undefined) {
            return;
        }
        this.clickThingCallback(thing);
    }
}
